import { useMemo, useRef, useState } from "react";
import { Box, Center, Container, HStack, VStack } from "styled-system/jsx";
import { css } from "styled-system/css";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconCircleCheck, IconCircleDashed } from "@tabler/icons-react";

import { Button, Input } from "~/components";
import { useToast } from "~/context/Toast";
import { dashboard, marketplace, mediaService, pos, upgrade, verification } from "~/services";
import type {
  DashboardChecklistItem,
  MarketplaceStatus,
  UpgradeRequestStatus,
  VerificationStatus,
  VerificationType,
} from "~/services";

// Angka 30 dipakai murni untuk tampilan (label & lebar bar) item "info".
// Status selesai/belum tetap mengikuti `done` dari server.
const CASHBOOK_TARGET_DAYS = 30;

const QUERY_KEY = {
  dashboard: ["dashboard"],
  verification: ["verification", "mine"],
  marketplace: ["marketplace", "mine"],
  upgrade: ["upgrade-request", "mine"],
  // Samakan dengan key di halaman Catatan Kasir supaya pencatatan kas baru
  // ikut menyegarkan progress streak yang tampil di dashboard.
  streak: ["pos", "streak"],
};

const VERIFICATION_LABEL: Record<VerificationStatus, string> = {
  pending: "Menunggu verifikasi",
  pending_manual: "Menunggu tinjauan manual",
  verified: "Terverifikasi",
  rejected: "Ditolak",
};

const MARKETPLACE_LABEL: Record<MarketplaceStatus, string> = {
  pending: "Menunggu pengecekan",
  verified: "Terverifikasi",
  invalid: "Tidak valid",
};

const UPGRADE_LABEL: Record<UpgradeRequestStatus, string> = {
  pending: "Menunggu peninjauan",
  approved: "Disetujui",
  rejected: "Ditolak",
};

type Tone = "success" | "warning" | "danger" | "neutral";

const StatusPill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className={css({
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      px: "12px",
      py: "6px",
      rounded: "full",
      textStyle: "xs",
      fontWeight: "bold",
      bg:
        tone === "success"
          ? "#E6F7EE"
          : tone === "warning"
            ? "#FFF7E6"
            : tone === "danger"
              ? "#FDECEC"
              : "secondary.lighten2",
      color:
        tone === "success" ? "primary" : tone === "warning" ? "warning" : tone === "danger" ? "error" : "secondary",
    })}
  >
    {children}
  </span>
);

const ProgressBar: React.FC<{ value: number }> = ({ value }) => (
  <Box className={css({ h: "8px", w: "100%", bg: "secondary.lighten2", rounded: "full", overflow: "hidden" })}>
    <Box
      style={{ width: `${value}%` }}
      className={css({ h: "100%", bg: "primary", rounded: "full", transition: "width 0.3s ease" })}
    />
  </Box>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={css({ textStyle: "xs", color: "secondary" })}>{children}</p>
);

// `http()` menolak dengan string pesan dari backend; tetap toleran bila
// suatu saat yang diterima berupa Error/objek.
const errorMessage = (e: unknown): string => {
  if (typeof e === "string" && e.trim()) return e;
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return m;
  }
  return "Terjadi kesalahan, coba lagi.";
};

export const DashboardUsaha = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Tautan toko yang sedang diketik pada item checklist "connect_account".
  const [storeUrl, setStoreUrl] = useState("");
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const dashboardQuery = useQuery({
    queryKey: QUERY_KEY.dashboard,
    queryFn: () => dashboard.getDashboard(),
  });

  const verificationQuery = useQuery({
    queryKey: QUERY_KEY.verification,
    queryFn: () => verification.getMine(),
  });

  const marketplaceQuery = useQuery({
    queryKey: QUERY_KEY.marketplace,
    queryFn: () => marketplace.getMine(),
  });

  const upgradeQuery = useQuery({
    queryKey: QUERY_KEY.upgrade,
    queryFn: () => upgrade.getMine(),
  });

  const streakQuery = useQuery({
    queryKey: QUERY_KEY.streak,
    queryFn: () => pos.getStreak(),
  });

  const summary = dashboardQuery.data?.data;
  const checklist = useMemo(() => summary?.checklist ?? [], [summary]);
  const verificationDocs = useMemo(() => verificationQuery.data?.data ?? [], [verificationQuery.data]);
  const stores = useMemo(() => marketplaceQuery.data?.data ?? [], [marketplaceQuery.data]);
  const streak = streakQuery.data?.data;

  // Pengajuan terbaru dipakai untuk menampilkan status di bawah tombol.
  const latestUpgrade = useMemo(() => {
    const list = upgradeQuery.data?.data ?? [];
    return [...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }, [upgradeQuery.data]);

  const failToast = (title: string, message: unknown) =>
    toast.error({
      title,
      description: (message as { message?: string })?.message ?? "Terjadi kesalahan, coba lagi.",
    });

  const uploadMutation = useMutation({
    mutationFn: async ({ type, file }: { type: VerificationType; file: File }) => {
      const uploaded = await mediaService.upload({ file });
      const fileUrl = uploaded?.data?.url;
      if (!fileUrl) throw new Error("Berkas gagal diunggah, coba lagi.");
      return verification.submit({ type, fileUrl });
    },
    onSuccess: (res) => {
      if (!res.success) {
        failToast("Gagal mengirim bukti", res.message);
        return;
      }
      toast.success({ title: "Bukti terkirim", description: "Dokumen Anda sedang diperiksa." });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.verification });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.dashboard });
    },
    onError: (e: unknown) =>
      toast.error({
        title: "Gagal mengirim bukti",
        description: errorMessage(e),
      }),
  });

  const connectMutation = useMutation({
    mutationFn: (url: string) => marketplace.connect(url),
    onSuccess: (res) => {
      if (!res.success) {
        failToast("Gagal menghubungkan toko", res.message);
        return;
      }
      // Server sudah nge-ping tautan, status pada respons ini sudah final.
      const store = res.data;
      const storeMessage = store.message?.trim();

      if (store.status === "verified") {
        setStoreUrl("");
        toast.success({ title: "Toko tersimpan", description: "Tautan toko Anda berhasil diverifikasi." });
      } else if (store.status === "invalid") {
        // Input sengaja tidak dikosongkan supaya user bisa langsung memperbaiki tautannya.
        toast.error({
          title: "Tautan toko tidak valid",
          description: storeMessage || "Tautan toko tidak dapat diakses. Periksa kembali URL Anda.",
        });
      } else {
        toast.create({
          type: "info",
          title: "Toko tersimpan",
          description: storeMessage || "Tautan toko Anda masih dalam pengecekan sistem.",
        });
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEY.marketplace });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.dashboard });
    },
    onError: (e: unknown) =>
      toast.error({
        title: "Gagal menghubungkan toko",
        description: errorMessage(e),
      }),
  });

  const upgradeMutation = useMutation({
    mutationFn: () => upgrade.submit(),
    onSuccess: (res) => {
      if (!res.success) {
        failToast("Gagal mengajukan", res.message);
        return;
      }
      toast.success({ title: "Pengajuan terkirim", description: "Pengajuan naik kelas sedang ditinjau admin." });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.upgrade });
      queryClient.invalidateQueries({ queryKey: QUERY_KEY.dashboard });
    },
    onError: (e: unknown) =>
      toast.error({
        title: "Gagal mengajukan",
        description: errorMessage(e),
      }),
  });

  if (dashboardQuery.isLoading) {
    return (
      <Center py="80px">
        <p className={css({ textStyle: "md", color: "secondary" })}>Memuat…</p>
      </Center>
    );
  }

  if (!summary) {
    return (
      <Center py="80px">
        <p className={css({ textStyle: "md", color: "secondary.darken8" })}>
          Data dashboard belum tersedia. Silakan muat ulang halaman.
        </p>
      </Center>
    );
  }

  const submitStoreUrl = () => {
    const url = storeUrl.trim();
    if (!url) {
      toast.error({ title: "Tautan kosong", description: "Isi tautan toko online Anda terlebih dahulu." });
      return;
    }
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      toast.error({ title: "Tautan tidak valid", description: "Tautan harus diawali http:// atau https://." });
      return;
    }
    connectMutation.mutate(url);
  };

  const renderUploadProof = (item: DashboardChecklistItem) => {
    const type = item.key as VerificationType;
    const doc = verificationDocs.find((d) => d.type === type);
    const isSubmitting = uploadMutation.isPending && uploadMutation.variables?.type === type;

    return (
      <VStack alignItems="flex-start" gap="12px">
        <input
          type="file"
          accept="image/*,application/pdf"
          style={{ display: "none" }}
          ref={(el) => {
            fileInputs.current[item.key] = el;
          }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) uploadMutation.mutate({ type, file });
          }}
        />
        <Button
          size="sm"
          variant="outline"
          loading={isSubmitting}
          disabled={isSubmitting}
          onClick={() => fileInputs.current[item.key]?.click()}
        >
          {doc ? "Unggah Ulang Bukti" : "Unggah Bukti"}
        </Button>

        {verificationQuery.isLoading ? (
          <Note>Memuat status dokumen…</Note>
        ) : doc ? (
          <VStack alignItems="flex-start" gap="6px">
            <HStack gap="8px">
              <span className={css({ textStyle: "xs", color: "secondary" })}>Status dokumen</span>
              <StatusPill
                tone={doc.status === "verified" ? "success" : doc.status === "rejected" ? "danger" : "warning"}
              >
                {VERIFICATION_LABEL[doc.status]}
              </StatusPill>
            </HStack>
            {doc.status === "rejected" && doc.note ? <Note>Alasan penolakan: {doc.note}</Note> : null}
          </VStack>
        ) : (
          <Note>Belum ada dokumen yang diunggah.</Note>
        )}
      </VStack>
    );
  };

  const renderConnectAccount = () => (
    <VStack alignItems="stretch" gap="12px">
      <HStack gap="12px" flexWrap="wrap" alignItems="center">
        <Input
          size="md"
          borderRadius="12"
          minW={{ base: "0", md: "360px" }}
          placeholder="https://shopee.co.id/toko-anda"
          value={storeUrl}
          onChange={(e) => setStoreUrl(e.target.value)}
          disabled={connectMutation.isPending}
        />
        <Button
          size="sm"
          loading={connectMutation.isPending}
          disabled={connectMutation.isPending}
          onClick={submitStoreUrl}
        >
          Hubungkan
        </Button>
      </HStack>

      {marketplaceQuery.isLoading ? (
        <Note>Memuat daftar toko…</Note>
      ) : stores.length === 0 ? (
        <Note>Belum ada toko online yang terhubung.</Note>
      ) : (
        <VStack alignItems="stretch" gap="8px">
          {stores.map((store) => (
            <Box
              key={store._id}
              rounded="12px"
              borderColor="secondary.lighten2"
              borderWidth="1px"
              borderStyle="solid"
              p="12px"
            >
              <HStack justifyContent="space-between" gap="12px" flexWrap="wrap">
                <VStack alignItems="flex-start" gap="2px">
                  <span className={css({ textStyle: "sm", fontWeight: "medium", color: "secondary.darken8" })}>
                    {store.platform ?? "Lainnya"}
                  </span>
                  <span className={css({ textStyle: "xs", color: "secondary", wordBreak: "break-all" })}>
                    {store.url}
                  </span>
                </VStack>
                <StatusPill
                  tone={store.status === "verified" ? "success" : store.status === "invalid" ? "danger" : "warning"}
                >
                  {MARKETPLACE_LABEL[store.status]}
                </StatusPill>
              </HStack>
              {store.status === "invalid" && store.message ? <Note>{store.message}</Note> : null}
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );

  const renderInfo = () => {
    const currentStreak = streak?.current_streak ?? 0;
    const streakProgress = Math.min(100, (currentStreak / CASHBOOK_TARGET_DAYS) * 100);

    return (
      <VStack alignItems="stretch" gap="12px">
        {streakQuery.isLoading ? (
          <Note>Memuat catatan kasir…</Note>
        ) : (
          <>
            <span className={css({ textStyle: "sm", fontWeight: "medium", color: "secondary.darken8" })}>
              {currentStreak} / {CASHBOOK_TARGET_DAYS} Hari Aktif Mencatat Kas
            </span>
            <ProgressBar value={streakProgress} />
          </>
        )}
        <Box>
          <Link to="/kasir">
            <Button size="sm" variant="outline">
              Buka Catatan Kasir
            </Button>
          </Link>
        </Box>
      </VStack>
    );
  };

  // Backend menolak pengajuan baru selama pengajuan sebelumnya masih pending,
  // jadi tombolnya ikut dimatikan supaya user tidak menekan klik yang pasti gagal.
  const hasPendingUpgrade = latestUpgrade?.status === "pending";

  const upgradeHelper = summary.is_max_level
    ? "Anda sudah berada di level tertinggi."
    : hasPendingUpgrade
      ? "Pengajuan kenaikan kelas Anda sebelumnya masih diproses. Tunggu hasil peninjauan admin."
      : summary.checklist_progress < 100
        ? "Selesaikan seluruh checklist (100%) untuk bisa mengajukan kenaikan kelas."
        : null;

  return (
    <Container maxW="7xl" py="40px">
      <VStack alignItems="stretch" gap="32px">
        {/* ---- Widget level ---- */}
        <Box rounded="24px" borderColor="secondary.lighten2" borderWidth="1px" borderStyle="solid" p="32px" w="100%">
          <HStack justifyContent="space-between" alignItems="flex-start" gap="16px" flexWrap="wrap">
            <VStack alignItems="flex-start" gap="4px">
              <span className={css({ textStyle: "sm", color: "secondary" })}>Level usaha Anda</span>
              <h1 className={css({ textStyle: "h6", fontWeight: "bold", color: "secondary.darken8" })}>
                {summary.level_name}
              </h1>
            </VStack>
            <StatusPill tone={summary.is_max_level ? "success" : "neutral"}>
              {summary.is_max_level ? "Level tertinggi" : `Menuju ${summary.next_level_name ?? "level berikutnya"}`}
            </StatusPill>
          </HStack>

          <HStack justifyContent="space-between" mt="24px" mb="8px">
            <span className={css({ textStyle: "sm", color: "secondary" })}>Progres checklist</span>
            <span className={css({ textStyle: "sm", fontWeight: "bold", color: "primary" })}>
              {summary.checklist_progress}%
            </span>
          </HStack>
          <ProgressBar value={summary.checklist_progress} />
        </Box>

        {/* ---- Checklist naik kelas ---- */}
        <VStack alignItems="stretch" gap="16px">
          <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8" })}>
            Checklist Naik Kelas
          </h2>

          {checklist.length === 0 ? (
            <Box
              rounded="24px"
              borderColor="secondary.lighten2"
              borderWidth="1px"
              borderStyle="solid"
              p="32px"
              textAlign="center"
            >
              <p className={css({ textStyle: "md", color: "secondary" })}>
                Belum ada checklist untuk level Anda saat ini.
              </p>
            </Box>
          ) : (
            checklist.map((item) => (
              <Box
                key={item.key}
                rounded="24px"
                borderColor="secondary.lighten2"
                borderWidth="1px"
                borderStyle="solid"
                p="32px"
              >
                <HStack justifyContent="space-between" alignItems="flex-start" gap="16px" flexWrap="wrap">
                  <VStack alignItems="flex-start" gap="8px">
                    <span
                      className={css({
                        bg: "#E6F7EE",
                        color: "primary",
                        px: "12px",
                        py: "4px",
                        rounded: "full",
                        textStyle: "xs",
                        fontWeight: "bold",
                      })}
                    >
                      {item.category}
                    </span>
                    <h3 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8" })}>
                      {item.title}
                    </h3>
                    <p className={css({ textStyle: "sm", color: "secondary" })}>{item.description}</p>
                  </VStack>

                  <HStack
                    gap="6px"
                    flexShrink="0"
                    className={css({ color: item.done ? "primary" : "secondary.lighten1" })}
                  >
                    {item.done ? <IconCircleCheck size={20} /> : <IconCircleDashed size={20} />}
                    <span className={css({ textStyle: "xs", fontWeight: "bold" })}>
                      {item.done ? "Selesai" : "Belum selesai"}
                    </span>
                  </HStack>
                </HStack>

                <Box mt="24px">
                  {item.action === "upload_proof"
                    ? renderUploadProof(item)
                    : item.action === "connect_account"
                      ? renderConnectAccount()
                      : renderInfo()}
                </Box>
              </Box>
            ))
          )}
        </VStack>

        {/* ---- Pengajuan kenaikan kelas ---- */}
        <Box rounded="24px" borderColor="secondary.lighten2" borderWidth="1px" borderStyle="solid" p="32px">
          <h2 className={css({ textStyle: "lg", fontWeight: "bold", color: "secondary.darken8", mb: "8px" })}>
            Ajukan Kenaikan Kelas
          </h2>
          <p className={css({ textStyle: "sm", color: "secondary", mb: "24px" })}>
            {summary.is_max_level
              ? "Usaha Anda sudah berada pada level tertinggi."
              : `Ajukan peninjauan untuk naik ke ${summary.next_level_name ?? "level berikutnya"}.`}
          </p>

          <Button
            onClick={() => upgradeMutation.mutate()}
            loading={upgradeMutation.isPending}
            disabled={!summary.can_submit_upgrade || hasPendingUpgrade || upgradeMutation.isPending}
          >
            Ajukan Kenaikan Kelas
          </Button>

          {upgradeHelper ? (
            <Box mt="12px">
              <Note>{upgradeHelper}</Note>
            </Box>
          ) : null}

          <Box mt="24px">
            {upgradeQuery.isLoading ? (
              <Note>Memuat status pengajuan…</Note>
            ) : latestUpgrade ? (
              <VStack alignItems="flex-start" gap="8px">
                <HStack gap="8px" flexWrap="wrap">
                  <span className={css({ textStyle: "sm", color: "secondary" })}>
                    Pengajuan terakhir ke Level {latestUpgrade.targetLevel}
                  </span>
                  <StatusPill
                    tone={
                      latestUpgrade.status === "approved"
                        ? "success"
                        : latestUpgrade.status === "rejected"
                          ? "danger"
                          : "warning"
                    }
                  >
                    {UPGRADE_LABEL[latestUpgrade.status]}
                  </StatusPill>
                </HStack>
                {latestUpgrade.status === "rejected" && latestUpgrade.note ? (
                  <Note>Alasan penolakan: {latestUpgrade.note}</Note>
                ) : null}
              </VStack>
            ) : (
              <Note>Belum ada riwayat pengajuan kenaikan kelas.</Note>
            )}
          </Box>
        </Box>
      </VStack>
    </Container>
  );
};
